/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionIssuersFetchMetadata } from "../funcs/remoteSessionIssuersFetchMetadata.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionIssuerDraft } from "../models/components/remotesessionissuerdraft.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  FetchRemoteSessionIssuerMetadataRequest,
  FetchRemoteSessionIssuerMetadataSecurity,
} from "../models/operations/fetchremotesessionissuermetadata.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type FetchRemoteSessionIssuerMetadataMutationVariables = {
  request: FetchRemoteSessionIssuerMetadataRequest;
  security?: FetchRemoteSessionIssuerMetadataSecurity | undefined;
  options?: RequestOptions;
};

export type FetchRemoteSessionIssuerMetadataMutationData =
  RemoteSessionIssuerDraft;

export type FetchRemoteSessionIssuerMetadataMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * fetchRemoteSessionIssuerMetadata remoteSessionIssuers
 *
 * @remarks
 * Hit an upstream issuer's RFC 8414 .well-known/oauth-authorization-server document and return a draft suitable for createRemoteSessionIssuer. Keyed by issuer URL; no record need exist and nothing is persisted. Use refreshMetadata to re-discover and persist against an existing issuer.
 */
export function useFetchRemoteSessionIssuerMetadataMutation(
  options?: MutationHookOptions<
    FetchRemoteSessionIssuerMetadataMutationData,
    FetchRemoteSessionIssuerMetadataMutationError,
    FetchRemoteSessionIssuerMetadataMutationVariables
  >,
): UseMutationResult<
  FetchRemoteSessionIssuerMetadataMutationData,
  FetchRemoteSessionIssuerMetadataMutationError,
  FetchRemoteSessionIssuerMetadataMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildFetchRemoteSessionIssuerMetadataMutation(client, options),
    ...options,
  });
}

export function mutationKeyFetchRemoteSessionIssuerMetadata(): MutationKey {
  return ["@gram/client", "remoteSessionIssuers", "fetchMetadata"];
}

export function buildFetchRemoteSessionIssuerMetadataMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: FetchRemoteSessionIssuerMetadataMutationVariables,
  ) => Promise<FetchRemoteSessionIssuerMetadataMutationData>;
} {
  return {
    mutationKey: mutationKeyFetchRemoteSessionIssuerMetadata(),
    mutationFn: function fetchRemoteSessionIssuerMetadataMutationFn({
      request,
      security,
      options,
    }): Promise<FetchRemoteSessionIssuerMetadataMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionIssuersFetchMetadata(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
